import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import clsx from 'clsx';
import SearchInput from '../../components/SearchInput';
import SearchableSelect from '../../components/SearchableSelect';
import { useTableSelection } from '../../hooks/useTableSelection';
import BulkActionBar, { type BulkAction } from '../../components/BulkActionBar';
import BulkConfirmDialog from '../../components/BulkConfirmDialog';
import { API_ORIGIN } from '../../api/baseUrl';
import {
  bulkUpdateProductCategory,
  bulkUpdateProductStatus,
  bulkDeleteProducts,
} from '../../api/adminClient';

// ============================================================================
// TYPES
// ============================================================================

interface Product {
  id: string;
  name: string;
  display_name: string | null;
  category: string | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

interface Category {
  id: string;
  name: string;
  parent_id: string | null;
  path: string; // e.g., "Software/Enterprise/CRM"
  level: number; // 0-8 (max 9 levels)
  icon: string;
  color: string;
}

// ============================================================================
// CONFIG
// ============================================================================

const ITEMS_PER_PAGE = 20;

const categoryColors = [
  { color: 'bg-blue-100 text-blue-800', icon: '🔷' },
  { color: 'bg-purple-100 text-purple-800', icon: '🏢' },
  { color: 'bg-green-100 text-green-800', icon: '🧩' },
  { color: 'bg-orange-100 text-orange-800', icon: '🛠️' },
  { color: 'bg-yellow-100 text-yellow-800', icon: '🔗' },
  { color: 'bg-pink-100 text-pink-800', icon: '💎' },
  { color: 'bg-indigo-100 text-indigo-800', icon: '⚡' },
  { color: 'bg-teal-100 text-teal-800', icon: '🌊' },
  { color: 'bg-gray-100 text-gray-800', icon: '📦' },
];

// Default categories (hierarchical)
const defaultCategories: Category[] = [
  { id: 'cat-1', name: 'Core Platform', parent_id: null, path: 'Core Platform', level: 0, icon: '🔷', color: 'bg-blue-100 text-blue-800' },
  { id: 'cat-2', name: 'Enterprise', parent_id: null, path: 'Enterprise', level: 0, icon: '🏢', color: 'bg-purple-100 text-purple-800' },
  { id: 'cat-3', name: 'Add-ons', parent_id: null, path: 'Add-ons', level: 0, icon: '🧩', color: 'bg-green-100 text-green-800' },
  { id: 'cat-4', name: 'Services', parent_id: null, path: 'Services', level: 0, icon: '🛠️', color: 'bg-orange-100 text-orange-800' },
  { id: 'cat-5', name: 'Integration', parent_id: null, path: 'Integration', level: 0, icon: '🔗', color: 'bg-yellow-100 text-yellow-800' },
];

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function getProducts(activeOnly: boolean = true): Promise<Product[]> {
  const url = activeOnly
    ? `${API_ORIGIN}/api/products`
    : `${API_ORIGIN}/api/products?active=false`;
  const res = await fetch(url);
  const json = await res.json();
  return json.data || [];
}

async function createProduct(data: Partial<Product>): Promise<Product> {
  const res = await fetch(`${API_ORIGIN}/api/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to create product');
  return json.data;
}

async function updateProduct(id: string, data: Partial<Product>): Promise<Product> {
  const res = await fetch(`${API_ORIGIN}/api/products/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Failed to update product');
  return json.data;
}

async function deleteProduct(id: string): Promise<void> {
  const res = await fetch(`${API_ORIGIN}/api/products/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const json = await res.json();
    throw new Error(json.error || 'Failed to delete product');
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getCategoryConfig(categoryPath: string | null, categories: Category[]): { label: string; color: string; icon: string } {
  if (!categoryPath) {
    return { label: 'Uncategorized', color: 'bg-gray-100 text-gray-800', icon: '📦' };
  }
  const category = categories.find(c => c.path === categoryPath || c.name === categoryPath);
  if (category) {
    return { label: category.name, color: category.color, icon: category.icon };
  }
  // Category was deleted or doesn't exist
  return { label: categoryPath, color: 'bg-red-100 text-red-800', icon: '⚠️' };
}

function isCategoryMissing(categoryPath: string | null, categories: Category[]): boolean {
  if (!categoryPath) return false;
  return !categories.some(c => c.path === categoryPath || c.name === categoryPath);
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AdminProducts() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>(defaultCategories);
  const [bulkDialog, setBulkDialog] = useState<string | null>(null);
  const [bulkCategory, setBulkCategory] = useState<string>('');
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const queryClient = useQueryClient();

  // Fetch products
  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: () => getProducts(false),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsCreating(false);
      setShowModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Product> }) => updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsEditing(false);
      setShowModal(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setSelectedProduct(null);
      setShowModal(false);
    },
  });

  // Filter products
  const filteredProducts = products?.filter((p) => {
    const matchesSearch =
      !searchTerm ||
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.display_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || p.category === categoryFilter;
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'active' ? p.is_active !== false : p.is_active === false);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const visibleProducts = filteredProducts?.slice(0, visibleCount);

  // Table selection
  const { selectedIds, toggleOne, toggleAll, clearSelection, isAllSelected, selectionCount } =
    useTableSelection(visibleProducts || []);

  const selectedItems = (products || []).filter((p) => selectedIds.has(p.id));

  const handleExportCSV = () => {
    const rows = (products || []).map((p) =>
      [p.display_name || p.name, p.name, p.category || '', p.description || '']
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const csv = ['display_name,internal_name,product_category,description', ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'products_export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const bulkActions: BulkAction[] = [
    { label: 'Change Category', icon: '🏷️', onClick: () => setBulkDialog('category') },
    { label: 'Activate', onClick: () => setBulkDialog('activate') },
    { label: 'Deactivate', onClick: () => setBulkDialog('deactivate') },
    { label: 'Delete', icon: '🗑️', variant: 'danger', onClick: () => setBulkDialog('delete') },
  ];

  // Get unique categories from products (for stats)
  const existingCategoryPaths = [...new Set(products?.map((p) => p.category).filter((c): c is string => Boolean(c)) || [])];
  const allCategoryPaths: string[] = [...new Set([...categories.map(c => c.path), ...existingCategoryPaths])];

  // Stats
  const stats = products
    ? {
        total: products.length,
        byCategory: allCategoryPaths.reduce((acc, cat) => {
          acc[cat] = products.filter((p) => p.category === cat).length;
          return acc;
        }, {} as Record<string, number>),
        missingCategory: products.filter(p => p.category && isCategoryMissing(p.category, categories)).length,
      }
    : null;

  // Top-level categories for stats display
  const topLevelCategories = categories.filter(c => c.level === 0);

  const handleOpenModal = (product: Product | null, creating: boolean) => {
    setSelectedProduct(product);
    setIsCreating(creating);
    setIsEditing(creating);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
    setIsCreating(false);
    setIsEditing(false);
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700">
        Failed to load products: {(error as Error).message}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Products</h1>
          <p className="text-gray-500 mt-1">Manage your product catalog</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Export Button */}
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition-colors"
          >
            <span>📤</span>
            <span>Export CSV</span>
          </button>
          {/* Import Button */}
          <button
            onClick={() => navigate('/admin/import?type=products')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition-colors"
          >
            <span>📥</span>
            <span>Import Product Data</span>
          </button>
          {/* Update Category Button */}
          <button
            onClick={() => setShowCategoryModal(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 transition-colors"
          >
            <span>🏷️</span>
            <span>Update Category</span>
          </button>
          {/* Add Product Button */}
          <button
            onClick={() => handleOpenModal(null, true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <span>+</span> Add Product
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <div
            className={clsx(
              "bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all hover:ring-2 hover:ring-primary-500",
              !categoryFilter && "ring-2 ring-primary-500"
            )}
            onClick={() => { setCategoryFilter(''); setVisibleCount(ITEMS_PER_PAGE); }}
          >
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total Products</div>
          </div>
          {topLevelCategories.slice(0, 5).map((cat) => {
            const count = stats.byCategory[cat.path] || 0;
            return (
              <div
                key={cat.id}
                className={clsx(
                  "bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all",
                  categoryFilter === cat.path && "ring-2 ring-primary-500"
                )}
                onClick={() => { setCategoryFilter(categoryFilter === cat.path ? '' : cat.path); setVisibleCount(ITEMS_PER_PAGE); }}
              >
                <div className="flex items-center gap-2">
                  <span>{cat.icon}</span>
                  <span className="text-2xl font-bold text-gray-900">{count}</span>
                </div>
                <div className="text-sm text-gray-500 truncate">{cat.name}</div>
              </div>
            );
          })}
          {/* Missing Category Warning */}
          {stats.missingCategory > 0 && (
            <div className="bg-red-50 rounded-xl shadow-sm border border-red-200 p-4">
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span className="text-2xl font-bold text-red-700">{stats.missingCategory}</span>
              </div>
              <div className="text-sm text-red-600">Missing Category</div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <SearchInput
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setVisibleCount(ITEMS_PER_PAGE); }}
              placeholder="Search by name or description..."
            />
          </div>
          <SearchableSelect
            value={categoryFilter}
            onChange={(v) => { setCategoryFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map((cat) => ({ value: cat.path, label: `${'  '.repeat(cat.level)}${cat.icon} ${cat.name}` })),
            ]}
            placeholder="All Categories"
            searchPlaceholder="Filter categories..."
            className="w-52"
          />
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setVisibleCount(ITEMS_PER_PAGE); }}
            options={[
              { value: '', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            placeholder="All Status"
            searchPlaceholder="Filter status..."
            className="w-48"
          />
          {(categoryFilter || statusFilter) && (
            <button
              onClick={() => { setCategoryFilter(''); setStatusFilter('active'); setVisibleCount(ITEMS_PER_PAGE); }}
              className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Product List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading...</div>
        ) : !visibleProducts || visibleProducts.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {searchTerm || categoryFilter || statusFilter ? 'No products match your filters' : 'No products found'}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleAll}
                    className="rounded border-gray-300"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleProducts.map((product) => {
                const config = getCategoryConfig(product.category, categories);
                const hasMissingCategory = isCategoryMissing(product.category, categories);
                return (
                  <tr
                    key={product.id}
                    className={clsx(
                      'cursor-pointer transition-colors hover:bg-gray-50',
                      !product.is_active && 'opacity-50'
                    )}
                    onClick={() => handleOpenModal(product, false)}
                  >
                    <td className="px-3 py-4 w-10">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(product.id)}
                        onChange={() => toggleOne(product.id)}
                        className="rounded border-gray-300"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{product.display_name || product.name}</div>
                        {product.display_name && product.display_name !== product.name && (
                          <div className="text-xs text-gray-400">{product.name}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {product.category ? (
                        <div className="flex items-center gap-2">
                          <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium', config.color)}>
                            {config.icon} {config.label}
                          </span>
                          {hasMissingCategory && (
                            <span className="text-red-500 text-sm" title="Category no longer exists">
                              ⚠️
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {product.description || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx(
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      )}>
                        {product.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Show More Button */}
        {filteredProducts && filteredProducts.length > visibleCount && (
          <div className="p-4 border-t border-gray-100 text-center">
            <button
              onClick={() => setVisibleCount((prev) => prev + ITEMS_PER_PAGE)}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Show More ({filteredProducts.length - visibleCount} remaining)
            </button>
          </div>
        )}

        {/* Showing count */}
        {filteredProducts && filteredProducts.length > 0 && (
          <div className="p-3 bg-gray-50 border-t border-gray-100 text-center text-sm text-gray-500">
            Showing {Math.min(visibleCount, filteredProducts.length)} of {filteredProducts.length} products
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectionCount={selectionCount}
        actions={bulkActions}
        onClear={clearSelection}
      />

      {/* Bulk Confirm Dialogs */}
      {bulkDialog === 'category' && (
        <BulkConfirmDialog
          title="Change Product Category"
          description="Update the category for selected products"
          items={selectedItems.map((p) => ({ id: p.id, label: p.display_name || p.name }))}
          confirmLabel="Update Category"
          confirmDisabled={!bulkCategory}
          onConfirm={async () => {
            if (!bulkCategory) return { succeeded: 0, failed: 0 };
            const res = await bulkUpdateProductCategory(
              selectedItems.map((p) => p.id),
              bulkCategory
            );
            queryClient.invalidateQueries({ queryKey: ['products'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => { setBulkDialog(null); setBulkCategory(''); }}
        >
          <select
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.path}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
        </BulkConfirmDialog>
      )}

      {bulkDialog === 'activate' && (
        <BulkConfirmDialog
          title="Activate Products"
          description="Set selected products to active"
          items={selectedItems.map((p) => ({ id: p.id, label: p.display_name || p.name }))}
          confirmLabel="Activate"
          onConfirm={async () => {
            const res = await bulkUpdateProductStatus(selectedItems.map((p) => p.id), true);
            queryClient.invalidateQueries({ queryKey: ['products'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'deactivate' && (
        <BulkConfirmDialog
          title="Deactivate Products"
          description="Set selected products to inactive"
          items={selectedItems.map((p) => ({ id: p.id, label: p.display_name || p.name }))}
          confirmLabel="Deactivate"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkUpdateProductStatus(selectedItems.map((p) => p.id), false);
            queryClient.invalidateQueries({ queryKey: ['products'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {bulkDialog === 'delete' && (
        <BulkConfirmDialog
          title="Delete Products"
          description="This will deactivate the selected products"
          items={selectedItems.map((p) => ({ id: p.id, label: p.display_name || p.name }))}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={async () => {
            const res = await bulkDeleteProducts(selectedItems.map((p) => p.id));
            queryClient.invalidateQueries({ queryKey: ['products'] });
            return { succeeded: res.succeeded, failed: res.failed };
          }}
          onClose={() => setBulkDialog(null)}
        />
      )}

      {/* Product Modal */}
      {showModal && (
        <ProductModal
          product={isCreating ? null : selectedProduct}
          isEditing={isEditing}
          isCreating={isCreating}
          categories={categories}
          onEdit={() => setIsEditing(true)}
          onCancel={() => {
            if (isCreating) {
              handleCloseModal();
            } else {
              setIsEditing(false);
            }
          }}
          onClose={handleCloseModal}
          onSave={(data) => {
            if (isCreating) {
              createMutation.mutate(data);
            } else if (selectedProduct) {
              updateMutation.mutate({ id: selectedProduct.id, data });
            }
          }}
          onDelete={() => {
            if (selectedProduct && confirm('Are you sure you want to deactivate this product?')) {
              deleteMutation.mutate(selectedProduct.id);
            }
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <CategoryManagementModal
          categories={categories}
          onClose={() => setShowCategoryModal(false)}
          onSave={(updatedCategories) => {
            setCategories(updatedCategories);
            setShowCategoryModal(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// PRODUCT MODAL COMPONENT
// ============================================================================

interface ProductModalProps {
  product: Product | null;
  isEditing: boolean;
  isCreating: boolean;
  categories: Category[];
  onEdit: () => void;
  onCancel: () => void;
  onClose: () => void;
  onSave: (data: Partial<Product>) => void;
  onDelete: () => void;
  isSaving: boolean;
}

function ProductModal({
  product,
  isEditing,
  isCreating,
  categories,
  onEdit,
  onCancel,
  onClose,
  onSave,
  onDelete,
  isSaving,
}: ProductModalProps) {
  const [form, setForm] = useState<{
    name: string;
    display_name: string;
    category: string;
    description: string;
  }>({
    name: product?.name || '',
    display_name: product?.display_name || product?.name || '',
    category: product?.category || '',
    description: product?.description || '',
  });

  const [syncNames, setSyncNames] = useState(
    isCreating || (product?.name === product?.display_name) || (!product?.display_name)
  );

  const isFormMode = isEditing || isCreating;
  const config = getCategoryConfig(product?.category || form.category, categories);
  const hasMissingCategory = product?.category ? isCategoryMissing(product.category, categories) : false;

  const handleSave = () => {
    onSave({
      name: form.name,
      display_name: form.display_name || form.name,
      category: form.category || null,
      description: form.description || null,
    });
  };

  // Build hierarchical category tree for selection
  const buildCategoryTree = (parentId: string | null, level: number): Category[] => {
    return categories
      .filter(c => c.parent_id === parentId)
      .flatMap(c => [c, ...buildCategoryTree(c.id, level + 1)]);
  };

  const orderedCategories = buildCategoryTree(null, 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isCreating ? 'Add New Product' : isEditing ? 'Edit Product' : (product?.display_name || product?.name)}
            </h2>
            {!isFormMode && hasMissingCategory && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <span>⚠️</span> Category no longer exists
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="space-y-4">
            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
              {isFormMode ? (
                <input
                  type="text"
                  value={form.display_name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      display_name: val,
                      ...(syncNames ? { name: val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') } : {}),
                    }));
                  }}
                  placeholder="Enter display name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  autoFocus
                />
              ) : (
                <p className="text-gray-900 py-2">{product?.display_name || product?.name}</p>
              )}
            </div>

            {/* Internal Name */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Internal Name *</label>
                {isFormMode && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={syncNames}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setSyncNames(checked);
                        if (checked) {
                          setForm((prev) => ({
                            ...prev,
                            name: prev.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
                          }));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    Same as display name
                  </label>
                )}
              </div>
              {isFormMode ? (
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Enter internal name (slug)"
                  disabled={syncNames}
                  className={clsx(
                    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none',
                    syncNames && 'bg-gray-50 text-gray-500'
                  )}
                />
              ) : (
                <p className="text-gray-500 py-2 text-sm font-mono">{product?.name}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              {isFormMode ? (
                <div className="space-y-2">
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                  >
                    <option value="">Select Category</option>
                    {orderedCategories.map((cat) => (
                      <option key={cat.id} value={cat.path}>
                        {'━'.repeat(cat.level)} {cat.icon} {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    Or type a new category name to create on-the-fly
                  </p>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="Or type new category..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                  />
                </div>
              ) : product?.category ? (
                <div className="flex items-center gap-2 py-2">
                  <span className={clsx('inline-flex items-center px-3 py-1 rounded-full text-sm font-medium', config.color)}>
                    {config.icon} {config.label}
                  </span>
                  {hasMissingCategory && (
                    <span className="text-red-500" title="Category no longer exists">⚠️</span>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 py-2">No category assigned</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              {isFormMode ? (
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Product description..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none h-24 resize-none"
                />
              ) : product?.description ? (
                <p className="text-gray-600 py-2 whitespace-pre-wrap">{product.description}</p>
              ) : (
                <p className="text-gray-400 py-2">No description</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          {product && !isFormMode && (
            <div className="mt-6 pt-4 border-t border-gray-100 text-sm text-gray-500">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400">Created:</span>{' '}
                  {format(new Date(product.created_at), 'MMM d, yyyy h:mm a')}
                </div>
                <div>
                  <span className="text-gray-400">Status:</span>{' '}
                  <span className={product.is_active ? 'text-green-600' : 'text-gray-500'}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-6 mt-6 border-t border-gray-100">
            {isFormMode ? (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !form.name.trim() || !form.display_name.trim()}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {isSaving ? 'Saving...' : isCreating ? 'Create Product' : 'Save Changes'}
                </button>
                <button
                  onClick={onCancel}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onEdit}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  Edit Product
                </button>
                <button
                  onClick={onDelete}
                  className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium"
                >
                  Deactivate
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CATEGORY MANAGEMENT MODAL
// ============================================================================

interface CategoryManagementModalProps {
  categories: Category[];
  onClose: () => void;
  onSave: (categories: Category[]) => void;
}

function CategoryManagementModal({ categories, onClose, onSave }: CategoryManagementModalProps) {
  const [localCategories, setLocalCategories] = useState<Category[]>(categories);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParent, setNewCategoryParent] = useState<string>('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  // Build hierarchical tree for display
  const buildCategoryTree = (parentId: string | null, level: number): Category[] => {
    return localCategories
      .filter(c => c.parent_id === parentId)
      .flatMap(c => [c, ...buildCategoryTree(c.id, level + 1)]);
  };

  const orderedCategories = buildCategoryTree(null, 0);

  // Check if can add child (max 9 levels)
  const canAddChild = (parentId: string | null): boolean => {
    if (!parentId) return true;
    const parent = localCategories.find(c => c.id === parentId);
    return parent ? parent.level < 8 : true;
  };

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;
    if (!canAddChild(newCategoryParent || null)) {
      alert('Maximum 9 levels of nesting allowed');
      return;
    }

    const parent = newCategoryParent ? localCategories.find(c => c.id === newCategoryParent) : null;
    const level = parent ? parent.level + 1 : 0;
    const path = parent ? `${parent.path}/${newCategoryName.trim()}` : newCategoryName.trim();
    const colorConfig = categoryColors[level % categoryColors.length];

    const newCategory: Category = {
      id: `cat-${Date.now()}`,
      name: newCategoryName.trim(),
      parent_id: newCategoryParent || null,
      path,
      level,
      icon: colorConfig.icon,
      color: colorConfig.color,
    };

    setLocalCategories([...localCategories, newCategory]);
    setNewCategoryName('');
    setNewCategoryParent('');
  };

  const handleDeleteCategory = (id: string) => {
    // Check for children
    const hasChildren = localCategories.some(c => c.parent_id === id);
    if (hasChildren) {
      alert('Cannot delete category with subcategories. Delete subcategories first.');
      return;
    }
    setLocalCategories(localCategories.filter(c => c.id !== id));
  };

  const handleEditCategory = (id: string) => {
    const cat = localCategories.find(c => c.id === id);
    if (cat) {
      setEditingId(id);
      setEditingName(cat.name);
    }
  };

  const handleSaveEdit = () => {
    if (!editingId || !editingName.trim()) return;

    setLocalCategories(localCategories.map(c => {
      if (c.id === editingId) {
        // Update path for this category
        const parent = c.parent_id ? localCategories.find(p => p.id === c.parent_id) : null;
        const newPath = parent ? `${parent.path}/${editingName.trim()}` : editingName.trim();
        return { ...c, name: editingName.trim(), path: newPath };
      }
      return c;
    }));

    setEditingId(null);
    setEditingName('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Manage Categories</h2>
            <p className="text-sm text-gray-500">Create and organize product categories (max 9 levels)</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 text-2xl font-bold transition-colors"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Add New Category */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-gray-900 mb-3">Add New Category</h3>
            <div className="flex gap-3">
              <select
                value={newCategoryParent}
                onChange={(e) => setNewCategoryParent(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              >
                <option value="">Top Level</option>
                {orderedCategories.filter(c => c.level < 8).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {'━'.repeat(cat.level)} {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Category name..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
                onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              />
              <button
                onClick={handleAddCategory}
                disabled={!newCategoryName.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
            </div>
          </div>

          {/* Category List */}
          <div className="space-y-2">
            {orderedCategories.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No categories yet. Add your first category above.
              </div>
            ) : (
              orderedCategories.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  style={{ marginLeft: `${cat.level * 24}px` }}
                >
                  <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center', cat.color)}>
                    {cat.icon}
                  </span>
                  {editingId === cat.id ? (
                    <input
                      type="text"
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                    />
                  ) : (
                    <span className="flex-1 font-medium text-gray-900">{cat.name}</span>
                  )}
                  <span className="text-xs text-gray-400">Level {cat.level + 1}</span>
                  <div className="flex items-center gap-1">
                    {editingId === cat.id ? (
                      <>
                        <button
                          onClick={handleSaveEdit}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          ✓
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        >
                          ✕
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditCategory(cat.id)}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.id)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(localCategories)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Save Categories
          </button>
        </div>
      </div>
    </div>
  );
}
